/**
 * ProcessEventListener
 * 
 * Listens for process events from ChronoSight and triggers alerts when process metrics
 * match alert conditions. Handles events related to:
 * - Process memory usage
 * - Process CPU usage
 * - Process disk usage
 * 
 * @module ProcessEventListener
 */

import BaseEventListener from './BaseEventListener';
import { AlertType, ProcessEvents } from '../../models/AlertModels';

/**
 * Logger for ProcessEventListener - only essential logging
 */
const Logger = {
  log: () => {}, // No-op to silence informational logs
  warn: (...args) => console.warn('[ProcessEventListener]', ...args), // Keep warnings
  error: (...args) => console.error('[ProcessEventListener]', ...args), // Keep errors
  debug: () => {} // No-op to silence debug logs
};

/**
 * Listener for process events
 * @class ProcessEventListener
 * @extends BaseEventListener
 */
class ProcessEventListener extends BaseEventListener {
  /**
   * Creates an instance of ProcessEventListener
   * @param {Object} alertService - Service for alert operations
   */
  constructor(alertService) {
    super(alertService, 'Process');
    
    // Bind event handlers
    this._handleProcessEvent = this._handleProcessEvent.bind(this);
    
    // Track process metrics for deduplication and rate limiting
    this.processMetrics = new Map();
    this.lastProcessedTimestamps = new Map();
    
    // Rate limiting: don't process events too frequently for the same process metric
    this.processingThreshold = 5000; // ms between processing the same metric
    

  }

  /**
   * Set up event listeners for process events
   * @protected
   * @returns {Promise<void>}
   */
  async _setupEventListeners() {
    // Add event listener for process events
    window.addEventListener('process-update', this._handleProcessEvent);
    

  }

  /**
   * Remove event listeners for process events
   * @protected
   * @returns {Promise<void>}
   */
  async _removeEventListeners() {
    // Remove event listener for process events
    window.removeEventListener('process-update', this._handleProcessEvent);
    

  }

  /**
   * Handle process event
   * @private
   * @param {Event} event - The event object
   */
  async _handleProcessEvent(event) {
    if (!event.detail || !event.detail.serverId || !event.detail.data) {
      Logger.warn('Invalid process event received', event);
      return;
    }
    
    const { serverId, data, timestamp } = event.detail;
    
    // Safely handle timestamp conversion
    let eventTime;
    try {
      // First try to use the event_time from data if available
      if (data && data.event_time) {
        eventTime = data.event_time;
      } else if (timestamp && !isNaN(new Date(timestamp).getTime())) {
        // If timestamp is valid, convert it to ISO string
        eventTime = new Date(timestamp).toISOString();
      } else {
        // Fallback to current time if both are invalid
        eventTime = new Date().toISOString();
      }
    } catch (error) {
      Logger.warn('Error parsing timestamp, using current time', error);
      eventTime = new Date().toISOString();
    }
    
    // Extract process data from event
    const processData = this._extractProcessData(data);
    if (!processData) {
      Logger.warn('Failed to extract process data from event', data);
      return;
    }
    
    // Store the latest metrics for this process
    const processId = processData.pid || processData.id || 'unknown';
    const processKey = `${serverId}:${processId}`;
    
    this.processMetrics.set(processKey, {
      ...processData,
      serverId,
      timestamp: Date.now(),
      eventTime
    });
    
    // Process the event to check for alerts
    await this._processProcessMetrics(serverId, processData, eventTime);
  }

  /**
   * Extract process data from various event formats
   * @private
   * @param {Object} eventData - The raw event data
   * @returns {Object|null} - The extracted process metrics or null if not found
   */
  _extractProcessData(eventData) {
    if (!eventData) {
      Logger.warn('No event data to extract for process');
      return null;
    }
    
    Logger.debug('Extracting process data from event data:', JSON.stringify(eventData));
    
    // Handle different formats of Process data
    if (eventData.Process && typeof eventData.Process === 'object') {
      // Format 1: { Process: { pid: 123, name: "process", ... } }
      return eventData.Process;
    } else if (eventData.event_data && typeof eventData.event_data === 'object') {
      // Format 2: { event_data: { pid: 123, name: "process", ... } }
      return eventData.event_data;
    } else if (eventData.data && typeof eventData.data === 'object') {
      // Format 3: { data: { pid: 123, name: "process", ... } }
      return eventData.data;
    } else {
      // Format 4: Direct format where the process data is at the root level
      const hasProcessKeys = ['pid', 'name', 'cpu_usage', 'memory_usage'].some(key => key in eventData);
      
      if (hasProcessKeys) {
        Logger.debug('Found process metrics in event data directly');
        return eventData;
      }
    }
    
    // Last resort: check for common process-related fields with any data type
    if (eventData.pid || eventData.id || eventData.process_id) {
      Logger.debug('Found process ID but missing other metrics, creating partial data');
      return {
        pid: eventData.pid || eventData.id || eventData.process_id,
        name: eventData.name || eventData.process_name || 'unknown',
        ...eventData // Include all original fields too
      };
    }
    
    Logger.warn('Failed to extract any process metrics from event data');
    return null;
  }

  /**
   * Process metrics for a specific process
   * @private
   * @param {string} serverId - The ID of the server
   * @param {Object} processData - The process metrics
   * @param {string} eventTime - The timestamp of the event
   */
  async _processProcessMetrics(serverId, processData, eventTime) {
    const processId = processData.pid || processData.id || 'unknown';
    const processName = processData.name || 'unknown';
    
    // Process each metric separately to allow for different rate limits per metric
    for (const metricKey of ProcessEvents) {
      if (!(metricKey in processData)) {
        continue; // Skip metrics that don't exist in this process data
      }
      
      const metricValue = processData[metricKey];
      const metricId = `${serverId}:${processId}:${metricKey}`;
      const lastProcessed = this.lastProcessedTimestamps.get(metricId) || 0;
      const now = Date.now();
      
      // Skip if we've processed this metric recently (rate limiting)
      if (now - lastProcessed < this.processingThreshold) {
        continue;
      }
      
      // Convert memory_usage and disk_usage from bytes to GB
      let convertedValue = metricValue;
      if (metricKey === 'memory_usage' || metricKey === 'disk_usage') {
        // Convert bytes to GB (1 GB = 1024^3 bytes)
        convertedValue = Number((metricValue / (1024 * 1024 * 1024)).toFixed(2));

      }
      
      // Create event data for this specific metric
      const eventData = {
        event: metricKey,
        value: convertedValue,
        pid: processId,
        name: processName,
        serverId,
        timestamp: now,
        eventTime,
        process: processData // Include the full process data for context
      };
      
      // Process the event
      await this.handleEvent(eventData);
      
      // Update last processed timestamp
      this.lastProcessedTimestamps.set(metricId, now);
    }
  }

  /**
   * Handle an event by evaluating alerts and creating notifications
   * @param {Object} eventData - The event data
   * @returns {Promise<boolean>} True if the event was handled successfully
   */
  async handleEvent(eventData) {
    // Call the parent method to track statistics
    await super.handleEvent(eventData);
    
    if (!this.active) {

      return false;
    }
    
    try {

      
      // Validate that we have the required fields for alert evaluation
      if (!eventData.event) {
        Logger.warn('Event missing required field "event":', eventData);
        return false;
      }
      
      // Use the updated AlertService.evaluateEvent method which handles notification creation
      const { triggeredAlerts, notifications } = await this.alertService.evaluateEvent(
        eventData,
        AlertType.PROCESS,
        eventData.serverId
      );
      
      // Always log event processing for monitoring

      
      // Log triggered alerts
      if (triggeredAlerts.length > 0) {

        // No need to log each alert individually
      }
      
      // Log created notifications
      if (notifications.length > 0) {

        // No need to log each notification individually
      }
      
      return true;
    } catch (error) {
      Logger.error('Error handling process event', error);
      return false;
    }
  }
}

export default ProcessEventListener;
